import React, { useState, useEffect, useRef } from "react";
import { Send, Mic, MicOff, Volume2, Sparkles, Trash2, Loader2, Plus, MessagesSquare, Pencil, Check, X, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { aiChat, getChatHistory, api } from "../api";
import { probeMic, voiceErrorText, micBlockerText } from "../voice";

export default function Chat() {
  const [sessions, setSessions] = useState([]);
  const [currentSid, setCurrentSid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [ttsOn, setTtsOn] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const recogRef = useRef(null);
  const scrollerRef = useRef(null);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/ai/chat/sessions");
      setSessions(data);
      if (!currentSid && data[0]) {
        openSession(data[0].id);
      } else if (!currentSid && data.length === 0) {
        await createNew();
      }
    } catch {}
  };

  const openSession = async (sid) => {
    setCurrentSid(sid);
    try {
      const msgs = await getChatHistory(sid);
      setMessages(msgs);
    } catch { setMessages([]); }
  };

  const createNew = async () => {
    try {
      const { data } = await api.post("/ai/chat/sessions", { title: "New Chat" });
      setSessions(s => [data, ...s]);
      setCurrentSid(data.id);
      setMessages([]);
    } catch {}
  };

  const renameSession = async (sid, title) => {
    await api.put(`/ai/chat/sessions/${sid}`, { title });
    setSessions(ss => ss.map(s => s.id === sid ? { ...s, title } : s));
    setEditingId(null);
  };

  const deleteSession = async (sid) => {
    if (!confirm("Delete this chat?")) return;
    await api.delete(`/ai/chat/sessions/${sid}`);
    const remain = sessions.filter(s => s.id !== sid);
    setSessions(remain);
    if (currentSid === sid) {
      if (remain[0]) openSession(remain[0].id);
      else { setCurrentSid(null); setMessages([]); await createNew(); }
    }
  };

  useEffect(() => {
    loadSessions();
    // Listen for the hero-landing "Ask anything" preload event
    const onAsk = (e) => {
      const txt = (e && e.detail) || window.__HEXA_ASK_PRELOAD__;
      if (typeof txt === "string" && txt.trim()) {
        setInput(txt);
        try { delete window.__HEXA_ASK_PRELOAD__; } catch {}
      }
    };
    if (typeof window !== "undefined" && window.__HEXA_ASK_PRELOAD__) {
      setInput(window.__HEXA_ASK_PRELOAD__);
      try { delete window.__HEXA_ASK_PRELOAD__; } catch {}
    }
    window.addEventListener("hexa:ask", onAsk);
    return () => window.removeEventListener("hexa:ask", onAsk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const speak = (text) => {
    if (!ttsOn) return;
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.02; u.pitch = 1;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch {}
  };

  // Immediately silence the current utterance whenever TTS is toggled off,
  // OR when the chat window is closed / unmounted. Otherwise HEXA keeps
  // narrating from beyond the grave.
  useEffect(() => {
    if (!ttsOn) {
      try { window.speechSynthesis.cancel(); } catch {}
    }
  }, [ttsOn]);

  useEffect(() => {
    return () => {
      try { window.speechSynthesis.cancel(); } catch {}
      try { recogRef.current?.stop(); } catch {}
    };
  }, []);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending || !currentSid) return;
    setInput("");
    const wasFirstMessage = messages.length === 0;
    setMessages(m => [...m, { role: "user", content: t, ts: Date.now() }]);
    setSending(true);
    try {
      const r = await aiChat(currentSid, t);
      setMessages(m => [...m, { role: "assistant", content: r.reply, ts: Date.now() }]);
      speak(r.reply);
      // Refresh session list so new title + preview appear
      if (wasFirstMessage) {
        setTimeout(loadSessions, 400);
      } else {
        setSessions(ss => ss.map(s => s.id === currentSid ? { ...s, preview: r.reply.slice(0, 140), updated: new Date().toISOString() } : s));
      }
    } catch {
      setMessages(m => [...m, { role: "assistant", content: "I'm unavailable right now. Please try again.", ts: Date.now() }]);
    }
    setSending(false);
  };

  const startListening = async () => {
    setVoiceError("");
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setVoiceError("Voice input requires Chrome, Edge, or Safari. Your browser doesn't support speech recognition.");
      return;
    }
    if (!window.isSecureContext) {
      setVoiceError("Voice input only works on HTTPS.");
      return;
    }
    if (listening) { recogRef.current?.stop(); return; }
    try {
      if (navigator.permissions?.query) {
        const status = await navigator.permissions.query({ name: "microphone" });
        if (status.state === "denied") {
          setVoiceError("Microphone is blocked. Click the lock icon next to the URL → Site settings → allow Microphone, then reload.");
          return;
        }
      }
    } catch { /* permission API not supported */ }

    setVoiceError("Checking microphone…");
    const mic = await probeMic();
    const blocker = micBlockerText(mic.verdict);
    if (blocker) { setVoiceError(blocker); return; }
    setVoiceError("");
    const deadStream = mic.verdict === "silent";
    let sawAnyResult = false;
    let retriedNoSpeech = false;

    const r = new SR();
    r.lang = "en-US"; r.continuous = false; r.interimResults = true;
    r.onstart = () => setListening(true);
    r.onerror = (ev) => {
      const code = ev?.error || "unknown";
      // Chrome gives up after 2-3s of silence; retry once before complaining.
      if (code === "no-speech" && !sawAnyResult && !retriedNoSpeech && !deadStream) {
        retriedNoSpeech = true;
        setVoiceError("Still listening — speak now…");
        try { r.start(); return; } catch (_) { /* fall through */ }
      }
      setListening(false);
      if (code !== "aborted") setVoiceError(voiceErrorText(code, { deadStream }));
    };
    r.onend = () => setListening(false);
    r.onresult = (ev) => {
      let txt = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) txt += ev.results[i][0].transcript;
      setInput(txt);
      if (ev.results[ev.results.length - 1].isFinal) send(txt);
    };
    recogRef.current = r;
    try {
      r.start();
    } catch {
      setListening(false);
      setVoiceError("Could not start microphone. Close other tabs using the mic and try again.");
    }
  };

  const today = new Date().toDateString();
  const grouped = sessions.reduce((acc, s) => {
    const d = new Date(s.updated || s.created).toDateString();
    const key = d === today ? "Today" : d;
    (acc[key] = acc[key] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="h-full flex flex-col md:flex-row" data-testid="app-chat">
      {sidebarOpen && (
        <div className="w-full md:w-56 h-40 md:h-auto border-b md:border-b-0 md:border-r border-white/10 bg-black/30 flex flex-col">
          <div className="p-2 flex gap-1">
            <button data-testid="chat-new" onClick={createNew} className="btn-accent text-xs flex-1 flex items-center justify-center gap-1">
              <Plus size={12} /> New Chat
            </button>
            <button onClick={() => setSidebarOpen(false)} className="btn-ghost p-1.5" title="Hide sidebar">
              <PanelLeftClose size={12} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-2 pb-2">
            {Object.keys(grouped).length === 0 && (
              <div className="text-center text-white/40 text-xs py-8">No saved chats</div>
            )}
            {Object.entries(grouped).map(([k, list]) => (
              <div key={k} className="mb-2">
                <div className="text-[10px] uppercase tracking-widest text-white/40 px-2 py-1">{k}</div>
                {list.map(s => (
                  <div
                    key={s.id}
                    data-testid={`chat-session-${s.id}`}
                    onClick={() => openSession(s.id)}
                    className={`group cursor-pointer rounded px-2 py-1.5 mb-0.5 ${currentSid === s.id ? "bg-white/10" : "hover:bg-white/5"}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <MessagesSquare size={11} className="text-white/50 shrink-0" />
                      {editingId === s.id ? (
                        <input
                          autoFocus
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") renameSession(s.id, editingTitle);
                            if (e.key === "Escape") setEditingId(null);
                          }}
                          onBlur={() => renameSession(s.id, editingTitle)}
                          className="flex-1 bg-white/10 border border-white/20 rounded px-1.5 text-xs outline-none"
                        />
                      ) : (
                        <span className="text-xs truncate flex-1">{s.title || "New Chat"}</span>
                      )}
                      {editingId === s.id ? (
                        <button onClick={(e) => { e.stopPropagation(); renameSession(s.id, editingTitle); }}>
                          <Check size={10} className="text-emerald-400" />
                        </button>
                      ) : (
                        <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                          <button
                            data-testid={`chat-rename-${s.id}`}
                            onClick={(e) => { e.stopPropagation(); setEditingId(s.id); setEditingTitle(s.title || ""); }}
                          >
                            <Pencil size={10} className="text-white/60" />
                          </button>
                          <button
                            data-testid={`chat-delete-${s.id}`}
                            onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                          >
                            <Trash2 size={10} className="text-red-400" />
                          </button>
                        </div>
                      )}
                    </div>
                    {s.preview && (
                      <div className="text-[10px] text-white/40 truncate pl-4 mt-0.5">{s.preview}</div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
          <div className="flex items-center gap-2">
            {!sidebarOpen && (
              <button onClick={() => setSidebarOpen(true)} className="btn-ghost p-1.5" title="Show sidebar">
                <PanelLeftOpen size={12} />
              </button>
            )}
            <Sparkles size={14} color="#ff7a2e" />
            <span className="text-sm font-medium">{sessions.find(s => s.id === currentSid)?.title || "HEXA Chat"}</span>
            <span className="text-[10px] text-white/40 mono">claude-sonnet-4.5</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="chat-tts-toggle"
              onClick={() => setTtsOn(t => !t)}
              className={`btn-ghost text-xs flex items-center gap-1 ${ttsOn ? "text-[#ff7a2e]" : ""}`}
            >
              <Volume2 size={12} /> {ttsOn ? "Voice on" : "Voice off"}
            </button>
          </div>
        </div>

        <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center mt-16 text-white/40">
              <Sparkles size={32} className="mx-auto mb-3 text-[#ff7a2e]" strokeWidth={1.2} />
              <div className="text-sm">Hi, I'm HEXA. Ask me anything.</div>
              <div className="text-xs mt-1">This conversation auto-saves to your sidebar.</div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#ff7a2e] text-white rounded-br-sm"
                  : "bg-white/8 text-white rounded-bl-sm border border-white/10"
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white/8 border border-white/10 px-3 py-2 rounded-2xl text-sm flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> HEXA is thinking…
              </div>
            </div>
          )}
        </div>

        <div className="p-3 border-t border-white/10 flex flex-col gap-2">
          {voiceError && (
            <div
              data-testid="chat-voice-error"
              className="px-3 py-2 mono text-[10px] uppercase tracking-widest border border-[#f87171]/40 bg-[#f87171]/10 text-[#fca5a5] flex items-start gap-2"
            >
              <span className="text-[#f87171] shrink-0">// mic</span>
              <span className="flex-1 normal-case tracking-normal">{voiceError}</span>
              <button
                onClick={() => setVoiceError("")}
                className="text-[#fca5a5]/60 hover:text-[#fca5a5] shrink-0"
                aria-label="dismiss"
              >×</button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              data-testid="chat-mic"
              onClick={startListening}
              className={`w-10 h-10 rounded-full flex items-center justify-center border border-white/10 ${listening ? "bg-[#ff7a2e] pulse-amber" : "bg-white/5 hover:bg-white/10"}`}
            >
              {listening ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Message HEXA…"
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-white/30"
            />
            <button data-testid="chat-send" onClick={() => send()} className="btn-accent w-10 h-10 rounded-full flex items-center justify-center p-0">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
